import React from "react";
import { Instagram, Facebook, MapPin, Phone, Mail } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { venueInfo } from "../lib/i18n";
import { useHours } from "../hooks/useHours";
import { useContact } from "../hooks/useContact";

const Footer = () => {
  const { t, lang } = useLang();
  const { rows: hourRows } = useHours();
  const { contact } = useContact();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[color:var(--bordeaux)] text-[color:var(--cream)] mt-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <img
              src="/logo.jpg?v=2"
              alt="Caffè & Cucina Sorriso"
              className="w-48 h-48 sm:w-56 sm:h-56 object-cover"
              data-testid="footer-logo"
            />
            <h3 className="mt-8 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Caffè <span className="font-serif-accent text-[color:var(--honey)]">&</span>
              <br />
              Cucina <span className="font-serif-accent">Sorriso</span>
            </h3>
            <p className="mt-6 max-w-md text-base text-white/70 leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          <div className="md:col-span-3 space-y-3">
            <p className="overline text-white/50">{t.contact.address_label}</p>
            <p className="flex items-start gap-2">
              <MapPin size={16} strokeWidth={1.2} className="mt-1 text-white/60" />
              <span>{contact.address}</span>
            </p>
            <p className="flex items-center gap-2">
              <Phone size={16} strokeWidth={1.2} className="text-white/60" />
              <a
                href={`tel:${contact.phone.replace(/\s/g, "")}`}
                data-testid="footer-phone-link"
                className="link-underline"
              >
                {contact.phone}
              </a>
            </p>
            <p className="flex items-center gap-2">
              <Mail size={16} strokeWidth={1.2} className="text-white/60" />
              <a
                href={`mailto:${contact.email}`}
                data-testid="footer-email-link"
                className="link-underline"
              >
                {contact.email}
              </a>
            </p>
          </div>

          <div className="md:col-span-3 space-y-3">
            <p className="overline text-white/50">{t.contact.hours_label}</p>
            {hourRows.map((h) => (
              <div key={h.id} className="flex items-center justify-between text-sm">
                <span className="text-white/80">{h[`label_${lang}`] || h.label_it}</span>
                <span className="divider-dotted opacity-30" />
                <span className="text-white/60">{h.time}</span>
              </div>
            ))}
            <div className="flex items-center gap-4 pt-6">
              <a
                href={contact.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram-link"
                className="hover:text-[color:var(--gold)] transition-colors"
              >
                <Instagram size={18} strokeWidth={1.2} />
              </a>
              <a
                href={contact.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook-link"
                className="hover:text-[color:var(--gold)] transition-colors"
              >
                <Facebook size={18} strokeWidth={1.2} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-white/40">
          <p>
            © {new Date().getFullYear()} {venueInfo.name}. {t.footer.rights}
          </p>
          <p className="overline text-white/30">{t.footer.made_with}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
